import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { usePartNumberSort } from './usePartNumberSort';
import SubTabToolbar from './SubTabToolbar';
import { useSubTabControls } from './useSubTabControls';

interface InTransitItem {
  part_number: string;
  description: string;
  asn_number: string;
  quantity: number;
  supplier_name: string;
  warehouse_code: string;
  [key: string]: unknown;
}

interface InTransitSubTabProps {
  partNumber?: string;
}

export default function InTransitSubTab({ partNumber }: InTransitSubTabProps) {
  const [items, setItems] = useState<InTransitItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const { pageSize, setPageSize, searchInput, setSearchInput, search, page, setPage } = useSubTabControls(partNumber);
  const { ordering, icon: sortIcon, toggle: togglePartNumberSort } = usePartNumberSort(() => setPage(1));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (ordering) params['ordering'] = ordering;
      const res = await callOffAPI.getInTransit(params);
      const data = res.data as { results?: InTransitItem[]; items?: InTransitItem[]; count?: number; total_count?: number };
      setItems(data.results || data.items || []);
      setTotalCount(data.count || data.total_count || 0);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load in-transit data';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div>
      <SubTabToolbar
        search={searchInput}
        onSearchChange={setSearchInput}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        placeholder="Search by part number or ASN number…"
      />
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4">No in-transit data available.</p>
      ) : (
      <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50">
              <th
                className="text-left p-2 font-medium whitespace-nowrap cursor-pointer hover:bg-muted/70 select-none"
                onClick={togglePartNumberSort}
              >
                <div className="flex items-center gap-1">Item No. {sortIcon}</div>
              </th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Item/Service Description</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Container Description</th>
              <th className="text-right p-2 font-medium whitespace-nowrap">In Transit</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Supplier Name</th>
              <th className="text-left p-2 font-medium whitespace-nowrap">Warehouse Code</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, i) => (
              <tr key={i} className="border-b hover:bg-muted/30">
                <td className="p-2 font-mono text-xs whitespace-nowrap">{item.part_number}</td>
                <td className="p-2">{item.description}</td>
                <td className="p-2">{item.asn_number}</td>
                <td className="p-2 text-right">{item.quantity}</td>
                <td className="p-2">{item.supplier_name}</td>
                <td className="p-2 whitespace-nowrap">{item.warehouse_code}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-3">
          <p className="text-xs text-muted-foreground">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
